import { Avatar, IconButton, Typography } from '@mui/material';
import { createColumnHelper, RowData, Updater } from '@tanstack/react-table';
import {
  BenefitSet,
  PaginatedLocationList,
  PaginatedDepartmentList,
  PaginatedCostCenterList,
  PaginatedGroupList,
  EmployeeListItem,
  EmployeeBenefitSettingsShort,
  Location,
  CostCenter,
  Department,
  UpcomingBenefitSets,
  CompanyPaymentData
} from '@pxtra-client-lib-legacy/gen';
import { Chip, SelectOption } from '../../atomic';
import { ArrowRightIcon, CloseIcon, InfoIcon, WarningIcon } from '../../../icons';
import Util from '../../../../misc/util';
import { useTranslation } from 'react-i18next';
import { TooltipComponent } from '../../atomic/feedback';
import { i18n, TFunction } from 'i18next';
import { TableRowSelector } from '../shared/selection-checkbox';
import { getNextMonthHeaderSuffix, BENEFIT_KEYS } from '../shared/utils';
import { NoteCell } from './cells/note';
import HasMissingFieldsCell from './cells/hasMissingFieldsCell';
import { GroupsCell } from './cells/groupsCell';
import { ActionsCell } from './cells/actionsCell';
import { BudgetUpdatesCell } from './cells/budgetUpdates';
import { BenefitSetUpdatesCell } from './cells/benefitSetUpdates';
import { useCreateBenefitSet } from '../../../../pages/actions/createBenefitSet/hooks/useCreateBenefitSet';
import { useStore } from '../../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import {
  useCompanyCostCenters,
  useCompanyDepartments,
  useCompanyGroups,
  useCompanyLocations,
  usePaymentData
} from '../../../../queries/company';
import {
  useGetUpcomingBenefitSets,
  useListBenefitSet
} from '../../../../queries/benefit/benefitSet';
import { useMemo } from 'react';
import { BenefitsButtonCell } from './cells/benefitsButtonCell';
import { SnackbarKey } from 'notistack';
import { BenefitSetNameCell } from '../benefitSet/cells/benefit-set-cell';
import { RemoveActionCell } from './cells/removeActionCell';
import { useNavigate } from 'react-router';
import { checkHasCompanyBenefitSets } from '../../../util/pricingModel';
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select' | 'boolean';
    filterInputType?: 'number' | 'date';
    filterOptions?: Array<SelectOption>;
  }

  interface TableMeta<TData extends RowData> {
    getRowIsDisabled?: (row: TData) => boolean;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface IdentifiedColumnDef<TData extends RowData, TValue> {
    setFilterValue?: Updater<TValue>;
  }
}

export const getCurrentBenefitSetName = (
  upcomingBenefitSets: UpcomingBenefitSets[] | undefined,
  startYear: number | undefined,
  startMonth: number | undefined
): string => {
  if (!upcomingBenefitSets || upcomingBenefitSets.length === 0) {
    return '-';
  }

  const now = new Date();
  const targetDate = new Date(
    startYear === undefined || startMonth === undefined
      ? now.setMonth(now.getMonth() + 1, 1)
      : new Date(startYear, startMonth, 1).getTime()
  );
  targetDate.setHours(0, 0, 0, 0);

  const reversedBenefitSets = [...upcomingBenefitSets].reverse();

  for (const benefitSet of reversedBenefitSets) {
    const benefitSetStartDate = new Date(benefitSet.start_date);
    benefitSetStartDate.setHours(0, 0, 0, 0);

    if (benefitSetStartDate <= targetDate) {
      return benefitSet.name ?? '-';
    }
  }

  return '-';
};

const columnHelper = createColumnHelper<EmployeeListItem>();

const useCompanyColumnData = () => {
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';
  const { data: locations } = useCompanyLocations(companyId);
  const { data: departments } = useCompanyDepartments(companyId);
  const { data: costCenters } = useCompanyCostCenters(companyId);
  const { data: groups } = useCompanyGroups(companyId);
  const { data: benefitSets } = useListBenefitSet(companyId);
  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  return {
    companyStore,
    locations,
    departments,
    costCenters,
    groups,
    benefitSets,
    paymentData,
    hasBenefitSets: checkHasCompanyBenefitSets(
      paymentData?.pricing_model,
      paymentData?.booked_benefits
    )
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useNextMonthBenefitSetColumn = (cell?: any, enableCustomSort = true) => {
  const { t, i18n } = useTranslation();
  const { companyStore } = useStore();
  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  const { data: benefitSets } = useListBenefitSet(
    companyStore.company?.id ?? '',
    paymentData?.pricing_model
  );
  return columnHelper.accessor('next_month_benefit_set', {
    header: t('common:BENEFIT_SET') + getNextMonthHeaderSuffix(i18n),
    cell: cell ?? BenefitsButtonCell,
    enableSorting: true,
    sortingFn: enableCustomSort
      ? (rowA, rowB) => {
          const nameA = rowA.original.next_month_benefit_set ?? '';
          const nameB = rowB.original.next_month_benefit_set ?? '';
          return nameA.localeCompare(nameB);
        }
      : undefined,
    meta: {
      filterVariant: 'select',
      filterOptions: [
        { value: '-', text: '-' },
        ...(benefitSets?.results.map((bs) => ({
          value: bs.name ?? '',
          text: bs.name ?? ''
        })) ?? [])
      ]
    },
    filterFn: (row, columnId, filterValue): boolean => {
      const value: BenefitSet = row.getValue(columnId);
      if (!filterValue) return true;
      if (filterValue === '-') return !value;
      return value === filterValue;
    }
  });
};

interface BaseColumnOptions {
  t: TFunction;
  i18n: i18n;
  navigate?: ReturnType<typeof useNavigate>;
  locations?: PaginatedLocationList;
  departments?: PaginatedDepartmentList;
  costCenters?: PaginatedCostCenterList;
  groups?: PaginatedGroupList;
  paymentData?: CompanyPaymentData;
}

const createSelectColumn = () => {
  return columnHelper.display({
    id: 'select',
    header: ({ table }) => <TableRowSelector className="-mr-16" table={table} pageOnly={false} />,
    cell: ({ row, table }) => {
      const getRowIsDisabled = table.options.meta?.getRowIsDisabled;
      const isDisabled = getRowIsDisabled ? getRowIsDisabled(row.original) : false;
      return <TableRowSelector className="-mr-16" row={row} disabled={isDisabled} />;
    }
  });
};

const createEmployeeNamePositionColumn = (
  t: TFunction,
  navigate?: ReturnType<typeof useNavigate>
) => {
  return columnHelper.accessor('last_name', {
    header: t('common:EMPLOYEES'),
    id: 'employeeNamePosition',
    cell: (info) => (
      <div
        role="button"
        tabIndex={0}
        className={`flex items-center gap-3 ${navigate ? 'cursor-pointer' : ''}`}
        onClick={() => {
          if (navigate) navigate(`/employees/${info.row.original.id}/dashboard`);
        }}
        onKeyDown={() => {
          if (navigate) navigate(`/employees/${info.row.original.id}/dashboard`);
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            backgroundColor: 'white',
            width: 40,
            height: 40,
            borderRadius: '.75rem'
          }}
          src={info.row.original.profile_picture}
          alt={`${info.row.original.first_name} ${info.row.original.last_name}`}
          imgProps={{
            style: { objectFit: 'contain' }
          }}
        />
        <div className="flex flex-col text-midnight-main">
          <div className="text-base font-medium">
            {info.row.original.first_name} {info.row.original.last_name}
          </div>
          <div className="text-sm">{info.row.original.position_name}</div>
        </div>
      </div>
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  });
};

const createCompactEmployeeNameColumn = (t: TFunction) => {
  return columnHelper.accessor('last_name', {
    header: t('common:EMPLOYEES'),
    id: 'employeeNamePosition',
    cell: (info) => (
      <div className="flex flex-col text-midnight-main">
        <div className="text-base font-medium">
          {info.row.original.first_name} {info.row.original.last_name}
        </div>
        <div className="text-sm">{info.row.original.position_name}</div>
      </div>
    ),
    enableGlobalFilter: false,
    enableColumnFilter: false
  });
};

const createEmployeeNumberColumn = (t: TFunction) => {
  return columnHelper.accessor('employee_number', {
    header: t('employee:PERSONAL_NUMBER_ABBR'),
    cell: ({ row }) =>
      row.original.employee_number ? (
        <Typography>{row.original.employee_number}</Typography>
      ) : (
        <TooltipComponent title={t('employee:PERSONAL_NUMBER_TOOLTIP')} placement="left" arrow>
          <div>
            <WarningIcon sx={{ color: 'text.secondary' }} />
          </div>
        </TooltipComponent>
      ),
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.employee_number;
      const valueB = rowB.original.employee_number;

      if (!valueA && !valueB) return 0;
      if (!valueA) return 1;
      if (!valueB) return -1;

      return valueA.localeCompare(valueB, undefined, { numeric: true });
    },
    enableGlobalFilter: false,
    enableColumnFilter: false
  });
};

const createBudgetColumn = (t: TFunction, i18n: i18n) => {
  return columnHelper.accessor('available_benefit_budget_next_month', {
    header: t('common:BUDGET') + getNextMonthHeaderSuffix(i18n),

    cell: ({ getValue }) => (
      <Typography className="font-medium">{Util.formatCurrency(getValue())}</Typography>
    ),
    meta: {
      filterVariant: 'range'
    },
    filterFn: Util.currencyFilterFn
  });
};

const createStatusColumn = (t: TFunction, excludedStatuses: string[] = []) => {
  const allStatusOptions = [
    { value: 'INACTIVE', text: t('employee:INACTIVE') },
    { value: 'ACTIVE', text: t('employee:ACTIVE') },
    { value: 'PAUSED', text: t('employee:PAUSED') },
    { value: 'RESIGNED', text: t('employee:RESIGNED') },
    { value: 'INVITATION_PENDING_EMAIL', text: t('employee:INVITATION_PENDING_EMAIL') },
    { value: 'INVITATION_PENDING_PDF', text: t('employee:INVITATION_PENDING_PDF') },
    { value: 'DEACTIVATION_DELAYED', text: t('employee:DEACTIVATION_DELAYED') }
  ];

  return columnHelper.accessor('status', {
    header: t('common:STATUS'),
    cell: ({ getValue }) => (
      <Chip
        label={t(`employee:${getValue()?.toUpperCase()}`)}
        color={getValue() === 'ACTIVE' ? 'heaven' : 'neutral'}
        size="small"
      />
    ),
    meta: {
      filterVariant: 'select',
      filterOptions: allStatusOptions.filter((option) => !excludedStatuses.includes(option.value))
    },
    filterFn: 'equals'
  });
};

const createHasMissingFieldsColumn = (t: TFunction) => {
  return columnHelper.accessor('has_missing_fields', {
    header: t('employee:PROFILE_COMPLETED'),
    cell: HasMissingFieldsCell,
    meta: {
      filterVariant: 'select',
      filterOptions: [
        { value: false, text: t('employee:COMPLETE') },
        { value: true, text: t('employee:INCOMPLETE') }
      ]
    },
    filterFn: (row, columnId, filterValue): boolean => {
      const value = row.getValue(columnId) as boolean;
      if (filterValue === undefined || filterValue === '') return true;
      return filterValue === 'true' ? value : !value;
    }
  });
};

const createNoteColumn = (t: TFunction, hasBenefitSets = true, isBonusStandalone = false) => {
  return columnHelper.accessor('note', {
    header: t('common:HINTS'),
    cell: ({ getValue, row }) => (
      <div className="flex items-center">
        <NoteCell note={getValue()} />
        {!isBonusStandalone && <BudgetUpdatesCell employeeId={row.original.id ?? ''} />}
        {hasBenefitSets && <BenefitSetUpdatesCell employeeId={row.original.id ?? ''} />}
      </div>
    ),
    enableSorting: false,
    meta: {
      filterVariant: 'text'
    },
    filterFn: 'includesString'
  });
};

const createLocationColumn = (t: TFunction, locations?: PaginatedLocationList, visible = false) => {
  return columnHelper.accessor('location', {
    header: t('company:LOCATION'),
    cell: ({ row, getValue }) => {
      if (visible && locations) {
        return (
          <Typography>
            {locations.results.filter((l: Location) => l.id === row.original.location)[0]?.name ||
              ''}
          </Typography>
        );
      }
      return getValue();
    },
    meta: {
      filterVariant: 'select',
      filterOptions:
        locations?.results.map((l: Location) => ({
          value: l.id,
          text: l.name
        })) ?? []
    },
    filterFn: 'includesString'
  });
};

const createDepartmentColumn = (t: TFunction, departments?: PaginatedDepartmentList) => {
  return columnHelper.accessor('department', {
    header: t('employee:DEPARTMENT'),
    cell: ({ getValue }) => getValue(),
    meta: {
      filterVariant: 'select',
      filterOptions:
        departments?.results.map((d: Department) => ({
          value: d.id,
          text: d.name
        })) ?? []
    },
    filterFn: 'includesString'
  });
};

const createCostCenterColumn = (t: TFunction, costCenters?: PaginatedCostCenterList) => {
  return columnHelper.accessor('cost_center', {
    header: t('employee:COST_CENTER'),
    cell: ({ getValue }) => getValue(),
    meta: {
      filterVariant: 'select',
      filterOptions:
        costCenters?.results.map((cc: CostCenter) => ({
          value: cc.id,
          text: cc.name
        })) ?? []
    },
    filterFn: 'includesString'
  });
};

const createActiveBenefitColumn = (t: TFunction) => {
  return columnHelper.accessor('benefits', {
    id: 'active_benefit',
    header: t('common:ACTIVE_BENEFIT'),
    cell: () => null,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'select',
      filterOptions: BENEFIT_KEYS.map((key) => ({
        value: key,
        text: t(`benefits:${key}`)
      }))
    },
    filterFn: (row, _columnId, filterValue): boolean => {
      if (!filterValue) return true;
      const benefits = row.original.benefits;
      if (!benefits) return false;
      const setting = benefits[filterValue as keyof EmployeeBenefitSettingsShort];
      return setting?.active === true;
    }
  });
};

const createBudgetExhaustedColumn = (t: TFunction) => {
  return columnHelper.accessor('remaining_benefit_budget_next_month', {
    id: 'budget_exhausted',
    header: t('common:BUDGET_EXHAUSTED'),
    cell: () => null,
    enableGlobalFilter: false,
    enableSorting: false,
    meta: {
      filterVariant: 'select',
      filterOptions: [
        { value: 'yes', text: t('common:YES') },
        { value: 'no', text: t('common:NO') }
      ]
    },
    filterFn: (row, _columnId, filterValue): boolean => {
      if (!filterValue) return true;
      const remaining = row.original.remaining_benefit_budget_next_month ?? 0;
      return filterValue === 'yes' ? remaining <= 0 : remaining > 0;
    }
  });
};

const createEntryDateColumn = (t: TFunction) => {
  return columnHelper.accessor('entry_date', {
    id: 'entry_date',
    header: t('employee:START_DATE'),
    cell: () => null,
    enableGlobalFilter: false,
    enableSorting: false,
    meta: {
      filterVariant: 'range',
      filterInputType: 'date'
    },
    filterFn: (row, _columnId, filterValue): boolean => {
      const [min, max] = (filterValue as [string, string]) ?? [null, null];
      if (!min && !max) return true;
      const entryDate = row.original.entry_date;
      if (!entryDate) return false;
      if (min && entryDate < min) return false;
      if (max && entryDate > max) return false;
      return true;
    }
  });
};

const createGroupsColumn = (t: TFunction, groups: PaginatedGroupList | undefined) => {
  return columnHelper.accessor('member_groups', {
    header: t('company:GROUP' + (t('company:GROUPS') === t('company:GROUP') ? 'S' : '')),
    cell: (info) => <GroupsCell info={info} />,
    meta: {
      filterVariant: 'select',
      filterOptions: [
        { value: 'not_assigned', text: t('company:GROUPS_NOT_ASSIGNED') },
        ...(groups?.results.map((group) => ({
          value: group.id,
          text: group.name
        })) ?? [])
      ]
    },
    filterFn: (row, columnId, filterValue) => {
      const groups = row.getValue(columnId) as Array<{ id: string }>;
      if (!filterValue) return true;
      if (filterValue === 'not_assigned') {
        return !groups || groups.length === 0;
      }
      return groups.some((group) => group.id === filterValue);
    },
    sortingFn: (rowA, rowB) => {
      const groupsA = rowA.original.member_groups ?? [];
      const groupsB = rowB.original.member_groups ?? [];

      const nameA = groupsA[0]?.name ?? '';
      const nameB = groupsB[0]?.name ?? '';

      return nameA.localeCompare(nameB);
    },
    getUniqueValues: (row: EmployeeListItem) => {
      return row.member_groups?.map((group) => group.name) ?? [];
    }
  });
};

const createActionsColumn = (t: TFunction) => {
  return columnHelper.display({
    id: 'actions',
    header: t('employee:ACTIONS'),
    cell: (info) => <ActionsCell info={info} />
  });
};

const createEmployeeBaseColumns = (options: BaseColumnOptions) => {
  const { t, i18n, navigate, locations, departments, costCenters, groups, paymentData } = options;

  const hasBenefitSets = checkHasCompanyBenefitSets(
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );
  const isBonusStandalone = paymentData?.pricing_model === 'bonus_standalone';

  return {
    select: createSelectColumn(),
    employeeNamePosition: createEmployeeNamePositionColumn(t, navigate),
    compactEmployeeName: createCompactEmployeeNameColumn(t),
    employeeNumber: createEmployeeNumberColumn(t),
    budget: createBudgetColumn(t, i18n),
    isBonusStandalone,
    status: createStatusColumn(t),
    hasMissingFields: createHasMissingFieldsColumn(t),
    note: createNoteColumn(t, hasBenefitSets, isBonusStandalone),
    groups: createGroupsColumn(t, groups),
    location: createLocationColumn(t, locations),
    department: createDepartmentColumn(t, departments),
    costCenter: createCostCenterColumn(t, costCenters),
    activeBenefit: createActiveBenefitColumn(t),
    budgetExhausted: createBudgetExhaustedColumn(t),
    entryDate: createEntryDateColumn(t),
    actions: createActionsColumn(t)
  };
};

export const useEmployeeColumns = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { locations, departments, costCenters, groups, paymentData, hasBenefitSets } =
    useCompanyColumnData();
  const nextMonthBenefitSetColumn = useNextMonthBenefitSetColumn();

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      navigate,
      locations,
      departments,
      costCenters,
      groups,
      paymentData
    });

    return [
      baseColumns.select,
      baseColumns.employeeNamePosition,
      baseColumns.employeeNumber,
      ...(hasBenefitSets ? [nextMonthBenefitSetColumn] : []),
      ...(!baseColumns.isBonusStandalone ? [baseColumns.budget] : []),
      baseColumns.status,
      baseColumns.hasMissingFields,
      baseColumns.note,
      baseColumns.groups,
      baseColumns.actions,
      baseColumns.location,
      baseColumns.department,
      baseColumns.costCenter,
      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [
    t,
    i18n,
    navigate,
    locations,
    departments,
    costCenters,
    groups,
    paymentData,
    nextMonthBenefitSetColumn
  ]);
};

export const useEmployeeSelectionColumns = (isSummary = false) => {
  const { t, i18n } = useTranslation();
  const { groups } = useCompanyColumnData();
  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({ t, i18n, groups });

    const columns = [
      baseColumns.select,
      baseColumns.employeeNamePosition,
      baseColumns.employeeNumber,
      ...(!baseColumns.isBonusStandalone ? [baseColumns.budget] : []),
      baseColumns.status,
      baseColumns.hasMissingFields,
      baseColumns.groups
    ];

    return isSummary ? columns.slice(1) : columns;
  }, [t, i18n, isSummary, groups]);
};

export const useEmployeeSelectionBenefitSetColumns = () => {
  const { t, i18n } = useTranslation();
  const { newBenefitSet } = useCreateBenefitSet();
  const { locations, departments, costCenters, groups } = useCompanyColumnData();
  const nextMonthBenefitSetColumn = useNextMonthBenefitSetColumn(
    (info: { row: { original: EmployeeListItem } }) => (
      <BenefitSetNameCell name={info.row.original.next_month_benefit_set ?? '-'} />
    ),
    false
  );

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      locations,
      departments,
      costCenters,
      groups
    });

    return [
      baseColumns.select,
      baseColumns.employeeNamePosition,
      nextMonthBenefitSetColumn,
      baseColumns.budget,
      baseColumns.status,
      baseColumns.note,
      baseColumns.groups,
      baseColumns.location,
      baseColumns.department,
      baseColumns.costCenter,
      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [
    t,
    i18n,
    newBenefitSet.startYear,
    newBenefitSet.startMonth,
    locations,
    departments,
    costCenters,
    groups,
    nextMonthBenefitSetColumn
  ]);
};

export const useEmployeeAssignmentColumns = (
  onRemoveEmployee?: (employeeId: string) => void,
  onAddEmployee?: (employeeId: string) => void,
  currentRemovalSnackbarRef?: React.MutableRefObject<SnackbarKey | null>
) => {
  const { t, i18n } = useTranslation();
  const { newBenefitSet } = useCreateBenefitSet();
  const { companyStore } = useStore();

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({ t, i18n });

    return [
      baseColumns.employeeNamePosition,

      columnHelper.display({
        id: 'current_benefit_set',
        header: () => (
          <div className="flex items-center gap-1">
            <span>{t('benefitSet:currentBenefitSet')}</span>
            <TooltipComponent title={t('benefitSet:currentBenefitSetHint')} placement="top" arrow>
              <div>
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </div>
            </TooltipComponent>
          </div>
        ),
        cell: observer((info) => {
          const { data: upcomingBenefitSets } = useGetUpcomingBenefitSets(
            info.row.original.id ?? ''
          );
          const currentBenefitSetName = getCurrentBenefitSetName(
            upcomingBenefitSets,
            newBenefitSet.startYear,
            newBenefitSet.startMonth
          );

          return <BenefitSetNameCell name={currentBenefitSetName} />;
        })
      }),

      columnHelper.display({
        header: t('benefitSet:newBenefitSet'),
        cell: observer(() => {
          const benefitSet = companyStore.benefitSets?.find(
            (bs) => bs.id === newBenefitSet.editingBenefitSetId
          );
          const benefitSetName = benefitSet?.name_de ?? benefitSet?.name ?? '';

          return <BenefitSetNameCell name={benefitSetName} />;
        })
      }),

      columnHelper.display({
        header: t('benefitSet:assignmentMonth'),
        cell: observer(() => {
          if (newBenefitSet.startMonth === undefined || !newBenefitSet.startYear) {
            return <Typography className="font-medium">-</Typography>;
          }

          const date = new Date(newBenefitSet.startYear, newBenefitSet.startMonth, 1);
          const formattedDate = date.toLocaleString('default', { month: 'long', year: 'numeric' });

          return <Typography>{formattedDate}</Typography>;
        })
      }),

      columnHelper.display({
        id: 'actions',
        header: t('benefitSet:action'),
        cell: (info) => (
          <RemoveActionCell
            employeeId={info.row.original.id ?? ''}
            onRemoveEmployee={onRemoveEmployee}
            onAddEmployee={onAddEmployee}
            currentRemovalSnackbarRef={currentRemovalSnackbarRef}
          />
        )
      }),

      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [
    t,
    i18n,
    newBenefitSet,
    companyStore,
    onRemoveEmployee,
    onAddEmployee,
    currentRemovalSnackbarRef
  ]);
};

const createGroupActionColumn = (
  t: TFunction,
  icon: React.ReactElement,
  onAction: (employeeId: string) => void
) => {
  return columnHelper.display({
    id: 'groupAction',
    header: '',
    cell: (info) => (
      <IconButton
        size="small"
        sx={{
          cursor: 'pointer',
          ':hover': { color: 'grey.900' }
        }}
        onClick={() => onAction(info.row.original.id ?? '')}
      >
        {icon}
      </IconButton>
    )
  });
};

export const useGroupOverviewColumns = () => {
  const { t, i18n } = useTranslation();
  const { locations, departments, costCenters, groups, paymentData, hasBenefitSets } =
    useCompanyColumnData();
  const nextMonthBenefitSetColumn = useNextMonthBenefitSetColumn();

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      locations,
      departments,
      costCenters,
      groups,
      paymentData
    });

    return [
      baseColumns.employeeNamePosition,
      ...(hasBenefitSets ? [nextMonthBenefitSetColumn] : []),
      baseColumns.status,
      baseColumns.groups,
      baseColumns.location,
      baseColumns.department,
      baseColumns.costCenter
    ];
  }, [
    t,
    i18n,
    locations,
    departments,
    costCenters,
    groups,
    paymentData,
    nextMonthBenefitSetColumn
  ]);
};

export const useGroupSelectionColumns = (onMoveToGroup: (employeeId: string) => void) => {
  const { t, i18n } = useTranslation();
  const { locations, departments, costCenters, groups, paymentData, hasBenefitSets } =
    useCompanyColumnData();
  const nextMonthBenefitSetColumn = useNextMonthBenefitSetColumn();

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      locations,
      departments,
      costCenters,
      groups,
      paymentData
    });

    return [
      baseColumns.select,
      baseColumns.compactEmployeeName,
      ...(hasBenefitSets ? [nextMonthBenefitSetColumn] : []),
      baseColumns.status,
      baseColumns.groups,
      createGroupActionColumn(t, <ArrowRightIcon />, onMoveToGroup),
      baseColumns.location,
      baseColumns.department,
      baseColumns.costCenter,
      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [
    t,
    i18n,
    locations,
    departments,
    costCenters,
    groups,
    paymentData,
    nextMonthBenefitSetColumn,
    onMoveToGroup
  ]);
};

export const useGroupMemberColumns = (onRemoveFromGroup: (employeeId: string) => void) => {
  const { t, i18n } = useTranslation();
  const { locations, departments, costCenters, groups } = useCompanyColumnData();

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      locations,
      departments,
      costCenters,
      groups
    });

    return [
      baseColumns.compactEmployeeName,
      baseColumns.status,
      createGroupActionColumn(t, <CloseIcon />, onRemoveFromGroup),
      baseColumns.groups,
      baseColumns.location,
      baseColumns.department,
      baseColumns.costCenter,
      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [t, i18n, locations, departments, costCenters, groups, onRemoveFromGroup]);
};

export const useEmployeeBonusSelectionColumns = () => {
  const { t, i18n } = useTranslation();
  const { newBenefitSet } = useCreateBenefitSet();
  const { locations, departments, costCenters, groups } = useCompanyColumnData();
  const nextMonthBenefitSetColumn = useNextMonthBenefitSetColumn(
    ({ row }: { row: { original: EmployeeListItem } }) => row.original.next_month_benefit_set
  );

  return useMemo(() => {
    const baseColumns = createEmployeeBaseColumns({
      t,
      i18n,
      locations,
      departments,
      costCenters,
      groups
    });

    const locationColumn = createLocationColumn(t, locations, true);
    const statusColumn = createStatusColumn(t, ['RESIGNED', 'DEACTIVATION_DELAYED']);

    return [
      baseColumns.select,
      baseColumns.employeeNamePosition,
      baseColumns.employeeNumber,
      nextMonthBenefitSetColumn,
      statusColumn,
      baseColumns.groups,
      locationColumn,
      baseColumns.department,
      baseColumns.costCenter,
      baseColumns.activeBenefit,
      baseColumns.budgetExhausted,
      baseColumns.entryDate
    ];
  }, [
    t,
    i18n,
    locations,
    departments,
    costCenters,
    newBenefitSet,
    groups,
    nextMonthBenefitSetColumn
  ]);
};
